import React from "react";
import { Grid, Card, CardContent, Box, Typography } from "@mui/material";

const GraduateSummaryCards = ({
  educationLevelData,
  countryStudyData,
  employmentByFieldData,
  useEmploymentRate = false, // 👈 Control mode: true = employed dashboard, false = all graduates
}) => {
  const getBarColor = (pct) => {
    const numPct = parseFloat(pct);
    if (numPct >= 80) return "#4caf50"; // Green
    if (numPct >= 60) return "#2196f3"; // Blue
    if (numPct >= 40) return "#ff9800"; // Orange
    return "#f44336"; // Red
  };

  // ----------------------- Helper Function -----------------------
  const getPercentage = (item, total, key = "graduates") => {
    if (useEmploymentRate) return item.employmentRate;
    return ((item[key] / total) * 100).toFixed(2);
  };

  // ----------------------- Rendering Cards -----------------------
  const renderCategory = (title, data, labelKey, valueKey = "graduates") => {
    const total = data.reduce((sum, d) => sum + d[valueKey], 0);
    if (total === 0) return null;

    return (
      <Card sx={{ height: 450, display: "flex", flexDirection: "column" }}>
        <CardContent
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Box
            sx={{
              mt: 2,
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              pr: 8,
            }}
          >
            {data.map((item, index) => {
              const percentage = getPercentage(item, total, valueKey);

              return (
                <Box key={index} sx={{ mb: 3 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        minWidth: 120,
                        fontSize: "0.75rem",
                        fontWeight: "bold",
                        paddingRight: "30px",
                      }}
                    >
                      {item[labelKey]}
                    </Typography>
                    <Box
                      sx={{
                        position: "relative",
                        flex: 1,
                        height: 20,
                        bgcolor: "#f5f5f5",
                        borderRadius: 1,
                        overflow: "visible",
                        ml: 1,
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          height: "100%",
                          width: `${percentage}%`,
                          bgcolor: getBarColor(percentage),
                          borderRadius: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          pr: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: "white",
                            fontWeight: "bold",
                            fontSize: "0.7rem",
                            textShadow: "1px 1px 1px rgba(0,0,0,0.5)",
                          }}
                        >
                          {percentage}%
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          position: "absolute",
                          right: -60,
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#1976d2",
                          fontSize: "0.7rem",
                          fontWeight: "bold",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item[valueKey]?.toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </CardContent>
        <Box
          sx={{ p: 2, bgcolor: "#f5f5f5", borderTop: "1px solid #e0e0e0" }}
        >
          <Typography variant="subtitle2" gutterBottom>
            {title} Summary:
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Total: {total.toLocaleString()} | Highest:{" "}
            {data.length > 0 ? data[0][labelKey] : "N/A"} (
            {data.length > 0 ? getPercentage(data[0], total, valueKey) : "0"}%) |
            Lowest:{" "}
            {data.length > 0
              ? data[data.length - 1][labelKey]
              : "N/A"} (
            {data.length > 0
              ? getPercentage(data[data.length - 1], total, valueKey)
              : "0"}
            %)
          </Typography>
        </Box>
      </Card>
    );
  };

  // ----------------------- Layout -----------------------
  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} md={4}>
        {renderCategory("Education Level", educationLevelData, "level")}
      </Grid>
      <Grid item xs={12} md={4}>
        {renderCategory("Country of Study", countryStudyData, "studyCountry")}
      </Grid>
      <Grid item xs={12} md={4}>
        {renderCategory("Fields of Study", employmentByFieldData, "studyField")}
      </Grid>
    </Grid>
  );
};

export default GraduateSummaryCards;
